"use client";

import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { SubmitHandler, useForm } from "react-hook-form";
import { toast } from "react-toastify";

import { useRouter } from "next/navigation";

import { emailSchema } from "@/lib/zod";
import { handleApiError } from "@/utils/form-error";
import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, Form } from "react-bootstrap";
import { z } from "zod";

import AuthButton from "./auth-button";
import FloatingInput from "./floating-input";

import api, { ApiError } from "@/lib/api";

type FormFields = z.infer<typeof emailSchema>;

export default function ResendActivationForm() {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const router = useRouter();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormFields>({
    resolver: zodResolver(emailSchema),
  });

  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    if (!executeRecaptcha) {
      setError("root", {
        type: "manual",
        message: "reCAPTCHA not ready or failed to load.",
      });
      return;
    }

    try {
      const token = await executeRecaptcha("resend_activation");

      await api.post("/users/resend_activation/", {
        ...data,
        recaptcha: token,
      });

      toast.success(
        "If we have an account associated with the provided email address, we will send another confirmation email shortly. To complete the sign-up process, please click the link in the email. If you don't see the email, check your spam or junk folder. Ensure the email address you provided during sign-up is correct.",
      );

      router.push("/");
    } catch (error) {
      if (error instanceof ApiError) {
        handleApiError(error, setError);
      } else {
        setError("root", {
          type: "manual",
          message: "An unexpected error occurred while validating the reCAPTCHA. Please try again later.",
        });
      }
    }
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <FloatingInput type="email" label="Email Address" register={register("email")} error={errors.email} />
      {errors.root && <Alert variant="danger">{errors.root.message}</Alert>}
      <AuthButton title="Resend" isLoading={isSubmitting} />
    </Form>
  );
}
